import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDesignSystem } from '../../hooks/useDesignSystem';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { loginI18n } from './i18n';
import './login.scss';
import { normalizeApiError } from 'helpers/normalizeApiError';
import FloatingAlert from 'components/Alert/FloatingAlert';
import { getMsalInstance, loginRequest } from 'services/msalConfig';

const Login = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    mustChangePassword,
    login: authLogin,
    verifyMfa: authVerifyMfa,
    authLoading: authLoading,
  } = useAuth();
  const DesignSystemComponents = useDesignSystem();

  const { language } = useLanguage();
  const loginLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: loginI18n });

  useEffect(() => {
    loginI18n.changeLanguage(loginLanguage);
  }, [loginLanguage]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [alert, setAlert] = useState(() => {
    const reason = searchParams.get('reason');
    if (reason === 'inactivity') {
      return { type: 'warning', message: t('login_session_expired_inactivity') };
    }
    if (reason === 'entra_failed') {
      // Mensaje genérico a propósito: no revela el motivo (cuenta inexistente/inactiva) para evitar enumeración.
      return { type: 'danger', message: t('login_error_microsoft') };
    }
    return null;
  });

  const [showPassword, setShowPassword] = useState(false);

  // 'credentials' = paso 1 (email/password). 'mfa' = paso 2 (código del correo).
  const [step, setStep] = useState('credentials');
  const [mfaUserId, setMfaUserId] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  // false = vista por defecto (solo Microsoft); true = muestra el login clásico (correo/contraseña).
  const [showClassic, setShowClassic] = useState(false);

  const busy = authLoading;

  const handlePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setMfaCode('');
    setMfaUserId(null);
    setAlert(null);
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    if (busy) return;
    setAlert(null);

    if (!mfaCode.trim()) {
      setAlert({ type: 'danger', message: t('login_mfa_error_required') });
      return;
    }

    try {
      const response = await authVerifyMfa(mfaUserId, mfaCode);
      sessionStorage.removeItem('dailyStateCache');

      if (response?.mustChangePassword) {
        navigate('/cambiar-contrasena', { state: { oldPassword: password } });
        return;
      }

      navigate('/');
    } catch (err) {
      const normalized = normalizeApiError(err, t('login_mfa_error_invalid'));
      setAlert({ type: normalized.type, message: normalized.message });
    }
  };

  useEffect(() => {
    if (searchParams.has('reason')) {
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Cargar email recordado si existe
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Si ya está autenticado, redirigir a home (salvo que deba cambiar contraseña)
  useEffect(() => {
    if (isAuthenticated && !mustChangePassword) {
      navigate('/');
    }
  }, [isAuthenticated, mustChangePassword, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setAlert(null);

    try {
      if (!email.trim() || !password.trim()) {
        setAlert({
          type: 'danger',
          message: t('login_error_required'),
        });
        return;
      }

      if (rememberMe) localStorage.setItem('remembered_email', email);
      else localStorage.removeItem('remembered_email');

      const response = await authLogin(email, password);

      sessionStorage.removeItem('dailyStateCache');

      // MFA pendiente: pasamos al paso 2 (input del código). NO redirigir.
      if (response.requiresMfa) {
        setMfaUserId(response.userId);
        setMfaCode('');
        setStep('mfa');
        return;
      }

      if (response.mustChangePassword) {
        navigate('/cambiar-contrasena', { state: { oldPassword: password } });
        return;
      }
    } catch (err) {
      const normalized = normalizeApiError(
        err,
        t('login_error_fallback')
      );

      setAlert({
        type: normalized.type,
        message: normalized.message,
      });
    }
  };

  const handleClearError = () => {
    setAlert(null);
  };

  // Login con Microsoft (Entra ID / SSO) por REDIRECT: redirige la pestaña completa a
  // Microsoft. El retorno lo procesa index.js (handleRedirectPromise → /api/auth/entra-login).
  // Se usa redirect (no popup) para evitar el bloqueo por cookies de terceros / COOP del navegador.
  const handleLoginWithMicrosoft = async () => {
    if (busy) return;
    setAlert(null);

    try {
      const msalInstance = await getMsalInstance();
      await msalInstance.loginRedirect(loginRequest);
      // A partir de aquí la página navega a Microsoft; el flujo continúa en index.js al volver.
    } catch (err) {
      const normalized = normalizeApiError(err, t('login_error_fallback'));
      setAlert({ type: normalized.type, message: normalized.message });
    }
  };

  // Mostrar loader mientras se cargan los componentes del Design System
  if (!DesignSystemComponents) {
    return (
      <div className='login-container-loading'>
        <div className='loading-spinner' />
      </div>
    );
  }

  const { Card, Input, Button, Alert, Checkbox } = DesignSystemComponents;

  const isMfaStep = step === 'mfa';

  return (
    <div className='min-vh-100 d-flex align-items-center justify-content-center background-cover'>
      <Card className='card-login'>
        <div>
          <div className='text-center mb-6'>
            <p className='caption color-gray-600 mb-2 text-center'>Legal365</p>
            <h1 className='h4 mb-1 text-center'>
              {isMfaStep ? t('login_mfa_title') : t('login_title')}
            </h1>
            <p className='caption color-gray-600 mb-0 text-center'>
              {isMfaStep
                ? t('login_mfa_subtitle')
                : showClassic
                  ? t('login_subtitle')
                  : t('login_subtitle_microsoft')}
            </p>
          </div>
          {alert?.message && (
            <FloatingAlert
              type={alert.type}
              message={alert.message}
              onClose={handleClearError}
            />
          )}

          {isMfaStep ? (
            <form
              className='d-flex flex-column'
              onSubmit={handleVerifyMfa}
              noValidate
            >
              <div className=''>
                <Input
                  label={t('login_mfa_code_label')}
                  id='mfa-code'
                  type='text'
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  maxLength={6}
                  placeholder={t('login_mfa_code_placeholder')}
                  value={mfaCode}
                  onChange={(e) =>
                    setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  disabled={busy}
                />
              </div>
              <div className='d-flex align-items-center justify-content-between mt-3'>
                <Button
                  type='button'
                  label={t('login_mfa_btn_back')}
                  variant='naked'
                  size='sm'
                  disabled={busy}
                  onClick={handleBackToCredentials}
                />
                <Button
                  type='submit'
                  label={busy ? t('login_mfa_btn_loading') : t('login_mfa_btn_submit')}
                  variant='primary'
                  size='sm'
                  disabled={busy}
                />
              </div>
            </form>
          ) : !showClassic ? (
            /* Vista por defecto: solo "Iniciar sesión con Microsoft" (SSO). */
            <div className='d-flex flex-column'>
              <Button
                type='button'
                label={busy ? t('login_btn_loading') : t('login_btn_microsoft')}
                variant='primary'
                size='sm'
                disabled={busy}
                onClick={handleLoginWithMicrosoft}
              />
              <div className='mt-3 text-center'>
                <Button
                  type='button'
                  label={t('login_switch_to_classic')}
                  variant='naked'
                  size='sm'
                  disabled={busy}
                  onClick={() => setShowClassic(true)}
                />
              </div>
            </div>
          ) : (
            /* Vista clásica (correo/contraseña): se conserva, oculta tras el botón de arriba. */
            <form
              className='d-flex flex-column'
              onSubmit={handleSubmit}
              noValidate
            >
              <div className=''>
                <Input
                  label={t('login_email_label')}
                  id='email'
                  type='email'
                  placeholder={t('login_email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className='password-input-wrapper'>
                <Input
                  label={t('login_password_label')}
                  id='password'
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login_password_placeholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  icon={showPassword ? 'eye' : 'eyeSlash'}
                  iconOnClick={handlePasswordVisibility}
                />
              </div>
              <div className='d-flex align-items-center justify-content-between mb-3'>
                <Checkbox
                  id='rememberMe'
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={busy}
                  label={t('login_remember_me')}
                />
                <Button
                  type='button'
                  label={t('login_forgot_password')}
                  variant='naked'
                  size='sm'
                  disabled={busy}
                  onClick={() => navigate('/solicitud-nueva-contrasena')}
                />
              </div>
              <Button
                type='submit'
                label={busy ? t('login_btn_loading') : t('login_btn_submit')}
                variant='primary'
                size='sm'
                disabled={busy}
              />
              <div className='mt-3 text-center'>
                <Button
                  type='button'
                  label={t('login_back_to_microsoft')}
                  variant='naked'
                  size='sm'
                  disabled={busy}
                  onClick={() => setShowClassic(false)}
                />
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Login;
